import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Building2, User, Users, FileText, CheckCircle, AlertCircle, FileSpreadsheet, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { submitJVProperty, uploadToS3, getDraft, saveDraftToBackend, markDraftSubmitted } from '../utils/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const DRAFT_KEY = 'jv-draft-v1';
import { Step1PartnerInfo } from './jv-form-steps/Step1PartnerInfo';
import { Step2OwnerContact } from './jv-form-steps/Step2OwnerContact';
import { Step3PropertyDetails } from './jv-form-steps/Step3PropertyDetails';
import { Step4FinancialDocs } from './jv-form-steps/Step4FinancialDocs';
import { Step5SellerSituation } from './jv-form-steps/Step5SellerSituation';

interface JVSubmissionFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  // Step 1: Partner Information
  partnerFirstName: string;
  partnerLastName: string;
  partnerEmail: string;
  partnerPhone: string;
  partnerTaxCompany: string;
  taxAssistanceWebsite: string;
  // Step 2: Owner/Contact Information
  ownerFirstName: string;
  ownerLastName: string;
  ownerPhone: string;
  ownerEmail: string;
  mailingStreet: string;
  mailingLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  bestContactMethod: string;
  bestContactTime: string;
  dealType: string;

  // Step 3: Property Details
  propertyStreet: string;
  propertyLine2: string;
  propertyCity: string;
  propertyState: string;
  propertyZip: string;
  propertyCountry: string;
  propertyCounty: string;
  taxSaleDate: string;
  taxSaleAmount: string;
  countyTaxRules: string;
  publicTaxRecordLink: string;
  bedrooms: string;
  bathrooms: string;
  sqft: string;
  ownershipLength: string;
  hasInsurance: string;
  propertyCondition: string;
  repairCost: string;
  hasMortgage: string;
  mortgageBank: string;
  mortgageBalance: string;
  mortgagePayment: string;

  // Step 4: Financial Analysis & Documents
  arv: string;
  suggestedOffer: string;
  maoReports: File[];
  maoReportUrls: string[];
  propertyPhotos: File[];
  propertyPhotoUrls: string[];
  additionalDocs: File[];
  additionalDocUrls: string[];
  maoNotAvailable: boolean;
  photosNotAvailable: boolean;

  // Step 5: Seller Financial Situation
  employmentStatus: string;
  monthlyIncome: string;
  incomeBreakdown: string;
  utilityExpenses: string;
  additionalExpenses: string;
  expensesBreakdown: string;
  sellerRealistic: string;
  sellerWantsToStay: string;
  optionsDiscussed: string;
  jvSplit: string;
  jvSplitAgreements: boolean[];
  jvSplitChecklistVersion: string;
}

const STEPS = [
  { id: 1, title: 'Partner Info', icon: User },
  { id: 2, title: 'Property Details', icon: Building2 },
  { id: 3, title: 'Owner/Contact', icon: Users },
  { id: 4, title: 'Financial & Docs', icon: FileSpreadsheet },
  { id: 5, title: 'Seller Situation', icon: FileText },
];

function serializeDraft(data: FormData, step: number, sameAsProperty: boolean): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { maoReports, propertyPhotos, additionalDocs, ...serializable } = data;
  return { ...serializable, _step: step, _sameAsProperty: sameAsProperty };
}

export function JVSubmissionForm({ onClose, onSuccess }: JVSubmissionFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [mailingSameAsProperty, setMailingSameAsProperty] = useState(false);
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const localSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [formData, setFormData] = useState<FormData>({
    // Step 1: Partner Info
    partnerFirstName: '',
    partnerLastName: '',
    partnerEmail: '',
    partnerPhone: '',
    partnerTaxCompany: '',
    taxAssistanceWebsite: 'https://',

    // Step 2: Owner/Contact
    ownerFirstName: '',
    ownerLastName: '',
    ownerPhone: '',
    ownerEmail: '',
    mailingStreet: '',
    mailingLine2: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    bestContactMethod: '',
    bestContactTime: '',
    dealType: '',

    // Step 3: Property Details
    propertyStreet: '',
    propertyLine2: '',
    propertyCity: '',
    propertyState: '',
    propertyZip: '',
    propertyCountry: 'USA',
    propertyCounty: '',
    taxSaleDate: '',
    taxSaleAmount: '',
    countyTaxRules: '',
    publicTaxRecordLink: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    ownershipLength: '',
    hasInsurance: '',
    propertyCondition: '',
    repairCost: '',
    hasMortgage: '',
    mortgageBank: '',
    mortgageBalance: '',
    mortgagePayment: '',

    // Step 4: Financial & Documents
    arv: '',
    suggestedOffer: '',
    maoReports: [],
    maoReportUrls: [],
    propertyPhotos: [],
    propertyPhotoUrls: [],
    additionalDocs: [],
    additionalDocUrls: [],
    maoNotAvailable: false,
    photosNotAvailable: false,

    // Step 5: Seller Situation
    employmentStatus: '',
    monthlyIncome: '',
    incomeBreakdown: '',
    utilityExpenses: '',
    additionalExpenses: '',
    expensesBreakdown: '',
    sellerRealistic: '',
    sellerWantsToStay: '',
    optionsDiscussed: '',
    jvSplit: '',
    jvSplitAgreements: [false, false, false, false, false, false, false, false],
    jvSplitChecklistVersion: '2026-08-11',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  // On mount: load draft + profile, then apply both in one setState (avoids React 18 batching race)
  useEffect(() => {
    const init = async () => {
      // 1. Load draft (backend first, localStorage fallback)
      const backendDraft = await getDraft();
      const draft = backendDraft ?? (() => {
        try {
          const raw = localStorage.getItem(DRAFT_KEY);
          return raw ? JSON.parse(raw) : null;
        } catch { return null; }
      })();

      // 2. Fetch profile data in parallel with draft processing
      let profile: Record<string, string> | null = null;
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const response = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
          headers: { 'Authorization': `Bearer ${idToken?.toString()}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) profile = await response.json();
      } catch {
        // silent
      }

      // 3. Apply draft + profile in a SINGLE setState call so React 18 batching
      //    can't race the two updates against each other. Draft values win; profile
      //    only fills fields that the draft left empty.
      setFormData(prev => {
        let next = { ...prev };
        if (draft) {
          const { _step: _s, _sameAsProperty: _sp, ...saved } = draft as Record<string, unknown>;
          void _s; void _sp;
          next = { ...next, ...(saved as Partial<FormData>) };
        }
        if (profile) {
          next.partnerFirstName = next.partnerFirstName || profile.firstName;
          next.partnerLastName = next.partnerLastName || profile.lastName;
          next.partnerEmail = next.partnerEmail || profile.email;
          next.partnerPhone = next.partnerPhone || profile.phone;
          next.partnerTaxCompany = next.partnerTaxCompany || profile.company;
          // 'https://' is the URL input's default prefix — treat it as empty
          if (!next.taxAssistanceWebsite || next.taxAssistanceWebsite === 'https://') {
            next.taxAssistanceWebsite = profile.company_website || next.taxAssistanceWebsite;
          }
        }
        return next;
      });

      if (draft) {
        const { _step, _sameAsProperty } = draft as Record<string, unknown>;
        if (typeof _step === 'number') setCurrentStep(_step);
        if (typeof _sameAsProperty === 'boolean') setMailingSameAsProperty(_sameAsProperty);
      }
    };
    init();
  }, []);

  // Debounced localStorage auto-save on every form/step change
  useEffect(() => {
    if (localSaveTimer.current) clearTimeout(localSaveTimer.current);
    localSaveTimer.current = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(serializeDraft(formData, currentStep, mailingSameAsProperty)));
    }, 1500);
    return () => { if (localSaveTimer.current) clearTimeout(localSaveTimer.current); };
  }, [formData, currentStep, mailingSameAsProperty]);

  // When "Same as property address" is checked, keep the mailing address fields
  // live-synced to the property address — including when the user goes back to the
  // Property Details step and edits it. Unchecking stops the sync and retains values.
  useEffect(() => {
    if (!mailingSameAsProperty) return;
    setFormData(prev => ({
      ...prev,
      mailingStreet: prev.propertyStreet,
      mailingLine2: prev.propertyLine2,
      mailingCity: prev.propertyCity,
      mailingState: prev.propertyState,
      mailingZip: prev.propertyZip,
    }));
  }, [
    mailingSameAsProperty,
    formData.propertyStreet,
    formData.propertyLine2,
    formData.propertyCity,
    formData.propertyState,
    formData.propertyZip,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'jvSplit' ? { jvSplitAgreements: [false, false, false, false, false, false, false, false] } : {}),
    }));
  };

  const handleSplitAgreementChange = (index: number, checked: boolean) => {
    setFormData(prev => {
      const updated = [...prev.jvSplitAgreements];
      updated[index] = checked;
      return { ...prev, jvSplitAgreements: updated };
    });
  };

  const handleToggleMaoNotAvailable = () => {
    setFormData(prev => ({ ...prev, maoNotAvailable: !prev.maoNotAvailable }));
  };

  const handleTogglePhotosNotAvailable = () => {
    setFormData(prev => ({ ...prev, photosNotAvailable: !prev.photosNotAvailable }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(file => uploadToS3(file, 'property-photos'));
      const urls = await Promise.all(uploadPromises);

      setFormData({
        ...formData,
        propertyPhotos: [...formData.propertyPhotos, ...files],
        propertyPhotoUrls: [...formData.propertyPhotoUrls, ...urls],
      });
    } catch (err) {
      setError('Failed to upload photos. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMAOUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(file => uploadToS3(file, 'mao-reports'));
      const urls = await Promise.all(uploadPromises);

      setFormData({
        ...formData,
        maoReports: [...formData.maoReports, ...files],
        maoReportUrls: [...formData.maoReportUrls, ...urls],
      });
    } catch (err) {
      setError('Failed to upload MAO reports. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeMAOReport = (index: number) => {
    setFormData({
      ...formData,
      maoReports: formData.maoReports.filter((_, i) => i !== index),
      maoReportUrls: formData.maoReportUrls.filter((_, i) => i !== index),
    });
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(file => uploadToS3(file, 'documents'));
      const urls = await Promise.all(uploadPromises);

      setFormData({
        ...formData,
        additionalDocs: [...formData.additionalDocs, ...files],
        additionalDocUrls: [...formData.additionalDocUrls, ...urls],
      });
    } catch (err) {
      setError('Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removePhoto = (index: number) => {
    setFormData({
      ...formData,
      propertyPhotos: formData.propertyPhotos.filter((_, i) => i !== index),
      propertyPhotoUrls: formData.propertyPhotoUrls.filter((_, i) => i !== index),
    });
  };

  const removeDoc = (index: number) => {
    setFormData({
      ...formData,
      additionalDocs: formData.additionalDocs.filter((_, i) => i !== index),
      additionalDocUrls: formData.additionalDocUrls.filter((_, i) => i !== index),
    });
  };

  const canProceedToNextStep = (): boolean => {
    const isValidPhone = (phone: string) => phone.replace(/\D/g, '').length === 10;
    switch (currentStep) {
      case 1: // Partner Info
        return !!(formData.partnerFirstName && formData.partnerLastName &&
                  formData.partnerEmail && formData.partnerPhone &&
                  isValidPhone(formData.partnerPhone) &&
                  formData.partnerTaxCompany);
      case 2: // Property Details
        return !!(formData.propertyStreet && formData.propertyCity &&
                  formData.propertyState && formData.propertyZip &&
                  formData.propertyCounty && formData.taxSaleDate &&
                  formData.taxSaleAmount && formData.bedrooms &&
                  formData.bathrooms && formData.sqft && formData.hasInsurance &&
                  formData.propertyCondition && formData.repairCost && formData.hasMortgage);
      case 3: // Owner/Contact
        return !!(formData.ownerFirstName && formData.ownerLastName &&
                  formData.ownerPhone && isValidPhone(formData.ownerPhone) &&
                  formData.ownerEmail &&
                  formData.mailingStreet && formData.mailingCity &&
                  formData.mailingState && formData.mailingZip &&
                  formData.bestContactMethod && formData.bestContactTime && formData.dealType);
      case 4: // Financial & Documents
        return !!(formData.suggestedOffer && (formData.maoReportUrls.length > 0 || formData.maoNotAvailable));
      case 5: // Seller Situation
        if (!(formData.employmentStatus && formData.monthlyIncome &&
              formData.incomeBreakdown && formData.jvSplit)) return false;
        if (formData.jvSplit === '50/50') {
          return formData.jvSplitAgreements.every(Boolean);
        }
        return true;
      default:
        return false;
    }
  };

  // Friendly, human-readable list of what's still required on the current step.
  const getMissingFields = (): string[] => {
    const v = (x: any) => !!(x && String(x).trim());
    const miss: string[] = [];
    const need = (ok: boolean, label: string) => { if (!ok) miss.push(label); };
    switch (currentStep) {
      case 1:
        need(v(formData.partnerFirstName), 'Partner First Name');
        need(v(formData.partnerLastName), 'Partner Last Name');
        need(v(formData.partnerEmail), 'Partner Email');
        need(isValidPhone(formData.partnerPhone), 'Partner Phone (valid number)');
        need(v(formData.partnerTaxCompany), 'Partner Tax Company');
        break;
      case 2:
        need(v(formData.propertyStreet), 'Property Street');
        need(v(formData.propertyCity), 'Property City');
        need(v(formData.propertyState), 'Property State');
        need(v(formData.propertyZip), 'Property ZIP');
        need(v(formData.propertyCounty), 'Property County');
        need(v(formData.taxSaleDate), 'Tax Sale Date');
        need(v(formData.taxSaleAmount), 'Tax Sale Amount');
        need(v(formData.bedrooms), 'Bedrooms');
        need(v(formData.bathrooms), 'Bathrooms');
        need(v(formData.sqft), 'Square Footage');
        need(v(formData.hasInsurance), 'Has Insurance');
        need(v(formData.propertyCondition), 'Property Condition');
        need(v(formData.repairCost), 'Repair Cost');
        need(v(formData.hasMortgage), 'Has Mortgage');
        break;
      case 3:
        need(v(formData.ownerFirstName), 'Owner First Name');
        need(v(formData.ownerLastName), 'Owner Last Name');
        need(isValidPhone(formData.ownerPhone), 'Owner Phone (valid number)');
        need(v(formData.ownerEmail), 'Owner Email');
        need(v(formData.mailingStreet), 'Mailing Street');
        need(v(formData.mailingCity), 'Mailing City');
        need(v(formData.mailingState), 'Mailing State');
        need(v(formData.mailingZip), 'Mailing ZIP');
        need(v(formData.bestContactMethod), 'Best Contact Method');
        need(v(formData.bestContactTime), 'Best Contact Time');
        need(v(formData.dealType), 'Deal Type');
        break;
      case 4:
        need(v(formData.suggestedOffer), 'Suggested Offer');
        need(formData.maoReportUrls.length > 0 || formData.maoNotAvailable, 'MAO Report (upload or check "N/A — not available yet")');
        break;
      case 5:
        need(v(formData.employmentStatus), 'Employment Status');
        need(v(formData.monthlyIncome), 'Monthly Income');
        need(v(formData.incomeBreakdown), 'Income Breakdown');
        need(v(formData.jvSplit), 'Deal Split');
        if (formData.jvSplit === '50/50') {
          need(formData.jvSplitAgreements.every(Boolean), 'All 50/50 agreement checkboxes');
        }
        break;
    }
    return miss;
  };

  const pushDraftToBackend = async (data: FormData, step: number, sameAsProperty: boolean) => {
    setDraftStatus('saving');
    await saveDraftToBackend(serializeDraft(data, step, sameAsProperty));
    setDraftStatus('saved');
    setTimeout(() => setDraftStatus('idle'), 2000);
  };

  const handleSaveForLater = () => {
    pushDraftToBackend(formData, currentStep, mailingSameAsProperty);
  };

  const handleNext = () => {
    if (currentStep >= 5) return;
    if (canProceedToNextStep()) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setError(null);
      pushDraftToBackend(formData, nextStep, mailingSameAsProperty);
    } else {
      setMissingFields(getMissingFields());
      setShowValidationModal(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setError(null);
      pushDraftToBackend(formData, prevStep, mailingSameAsProperty);
    }
  };

  const handleSubmit = async () => {
    // Loud, friendly validation instead of a silently-disabled button
    if (!canProceedToNextStep()) {
      setMissingFields(getMissingFields());
      setShowValidationModal(true);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const submissionData = {
        // Partner Info
        partnerFirstName: formData.partnerFirstName,
        partnerLastName: formData.partnerLastName,
        partnerEmail: formData.partnerEmail,
        partnerPhone: formData.partnerPhone,
        partnerTaxCompany: formData.partnerTaxCompany,
        taxAssistanceWebsite: formData.taxAssistanceWebsite,
        // Owner/Contact
        ownerFirstName: formData.ownerFirstName,
        ownerLastName: formData.ownerLastName,
        ownerPhone: formData.ownerPhone,
        ownerEmail: formData.ownerEmail,
        mailingStreet: formData.mailingStreet,
        mailingLine2: formData.mailingLine2,
        mailingCity: formData.mailingCity,
        mailingState: formData.mailingState,
        mailingZip: formData.mailingZip,
        bestContactMethod: formData.bestContactMethod,
        bestContactTime: formData.bestContactTime,
        dealType: formData.dealType,

        // Property Details
        propertyStreet: formData.propertyStreet,
        propertyLine2: formData.propertyLine2,
        propertyCity: formData.propertyCity,
        propertyState: formData.propertyState,
        propertyZip: formData.propertyZip,
        propertyCountry: formData.propertyCountry,
        propertyCounty: formData.propertyCounty,
        taxSaleDate: formData.taxSaleDate,
        taxSaleAmount: formData.taxSaleAmount,
        countyTaxRules: formData.countyTaxRules,
        publicTaxRecordLink: formData.publicTaxRecordLink,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        sqft: formData.sqft,
        ownershipLength: formData.ownershipLength,
        hasInsurance: formData.hasInsurance,
        propertyCondition: formData.propertyCondition,
        repairCost: formData.repairCost,
        hasMortgage: formData.hasMortgage,
        mortgageBank: formData.mortgageBank,
        mortgageBalance: formData.mortgageBalance,
        mortgagePayment: formData.mortgagePayment,

        // Financial & Documents
        arv: formData.arv,
        suggestedOffer: formData.suggestedOffer,
        propertyPhotoUrls: formData.propertyPhotoUrls,
        maoReportUrls: formData.maoReportUrls,
        additionalDocUrls: formData.additionalDocUrls,

        // Seller Situation
        employmentStatus: formData.employmentStatus,
        monthlyIncome: formData.monthlyIncome,
        incomeBreakdown: formData.incomeBreakdown,
        utilityExpenses: formData.utilityExpenses,
        additionalExpenses: formData.additionalExpenses,
        expensesBreakdown: formData.expensesBreakdown,
        sellerRealistic: formData.sellerRealistic,
        sellerWantsToStay: formData.sellerWantsToStay,
        optionsDiscussed: formData.optionsDiscussed,

        // Deal Split
        jvSplit: formData.jvSplit,
        jvSplitChecklistVersion: formData.jvSplitChecklistVersion,
        jvSplitAgreements: formData.jvSplit === '50/50'
          ? formData.jvSplitAgreements.map((checked, i) => ({
              index: i,
              checked,
              agreedAt: checked ? new Date().toISOString() : null,
            }))
          : [],
      };

      await submitJVProperty(submissionData);
      // Clear localStorage draft; stamp backend draft with submittedAt for metrics history
      localStorage.removeItem(DRAFT_KEY);
      markDraftSubmitted();
      setSuccess(true);

      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit property');
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1PartnerInfo formData={formData} onChange={handleInputChange} />;

      case 2:
        return <Step3PropertyDetails formData={formData} onChange={handleInputChange} />;

      case 3:
        return (
          <Step2OwnerContact
            formData={formData}
            onChange={handleInputChange}
            sameAsProperty={mailingSameAsProperty}
            onSameAsPropertyChange={setMailingSameAsProperty}
          />
        );

      case 4:
        return (
          <Step4FinancialDocs
            formData={formData}
            onChange={handleInputChange}
            onMAOUpload={handleMAOUpload}
            onPhotoUpload={handlePhotoUpload}
            onDocUpload={handleDocUpload}
            removeMAOReport={removeMAOReport}
            removePhoto={removePhoto}
            removeDoc={removeDoc}
            isUploading={isUploading}
            onToggleMaoNotAvailable={handleToggleMaoNotAvailable}
            onTogglePhotosNotAvailable={handleTogglePhotosNotAvailable}
          />
        );

      case 5:
        return <Step5SellerSituation formData={formData} onChange={handleInputChange} onSplitAgreementChange={handleSplitAgreementChange} />;

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slide-in-left">
        {/* Header */}
        <div className="bg-gradient-to-r from-tdb-primary via-tdb-secondary to-tdb-primary text-white px-8 py-6 flex items-center justify-between sticky top-0 z-10 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <Building2 className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Submit Joint Venture Property</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Stepper */}
        <div className="bg-gray-50 px-8 py-4 border-b-2 border-gray-200">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      currentStep === step.id
                        ? 'bg-tdb-accent text-white scale-110 shadow-lg'
                        : currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 font-semibold ${
                    currentStep === step.id ? 'text-tdb-accent' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded transition-all ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content - Scrollable (flex-1 so the footer stays pinned + never clipped) */}
        <div className="flex-1 overflow-y-auto min-h-0 p-8">
          {renderStep()}

          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center space-x-3 p-4 bg-red-50 border-2 border-red-500 rounded-xl mt-6 animate-fade-in">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <div className="font-bold text-red-900">Error</div>
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}

          {success && (
            <div className="flex items-center space-x-3 p-4 bg-green-50 border-2 border-green-500 rounded-xl mt-6 animate-fade-in">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <div className="font-bold text-green-900">Property Submitted Successfully!</div>
                <div className="text-sm text-green-700">Your JV property has been added to the system.</div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="bg-gray-50 px-8 py-4 flex items-center justify-between border-t-2 border-gray-200">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1 || isSubmitting}
            className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          {/* Save for later + draft status */}
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleSaveForLater}
              disabled={isSubmitting || success || draftStatus === 'saving'}
              className="px-4 py-2 border-2 border-gray-300 rounded-xl text-gray-600 hover:bg-gray-100 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Save for later</span>
            </button>
            {draftStatus === 'saving' && (
              <span className="text-xs text-gray-400 animate-pulse">Saving…</span>
            )}
            {draftStatus === 'saved' && (
              <span className="text-xs text-green-600 flex items-center space-x-1">
                <CheckCircle className="w-3 h-3" />
                <span>Draft saved</span>
              </span>
            )}
          </div>

          {currentStep < 5 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={isUploading}
              className="px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all font-bold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>Next</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || success}
              className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-bold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner"></div>
                  <span>Submitting...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Submitted!</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Submit Property</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Loud-but-friendly "please complete these fields" modal */}
      {showValidationModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => setShowValidationModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-5 flex items-center space-x-3">
              <div className="bg-white/25 rounded-full p-2 flex-shrink-0">
                <AlertCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-white text-xl font-bold leading-tight">Almost there!</h3>
                <p className="text-white/90 text-sm">Just a few required fields to finish first.</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">Please complete these so we can save your deal:</p>
              <ul className="space-y-2.5 max-h-64 overflow-y-auto">
                {missingFields.map((f, i) => (
                  <li key={i} className="flex items-center space-x-3 text-gray-800 bg-orange-50 rounded-lg px-3 py-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></span>
                    <span className="font-medium text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setShowValidationModal(false)}
                className="mt-6 w-full bg-tdb-accent text-white py-3 rounded-xl font-bold hover:bg-[#0ea5ef] transition-all shadow-lg hover:shadow-xl"
              >
                Got it — I'll finish these
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
